import { AxiosPromise } from 'axios';

import { ItemStatsDto } from 'services/item/itemService.dto';
import PageableType from 'services/pageableType';
import { apiService, apiUrl, HEADER_AUTH_TOKEN } from 'services/utils/apiService';

import {
  BoughtItemDto,
  MeetupPointDto,
  MySoldPublicStatsDto,
  NewPurchaseDto,
  OrderConfigDto,
  PurchaseDto,
  PurchaseUpdateDto,
  SafetySpotDto,
  SoldItemDto,
} from './orderService.dto';

class OrderService {
  static BASE_URL = `${apiUrl}/api/order`;

  fetchItemsSoldCount(userId: number): AxiosPromise<MySoldPublicStatsDto> {
    const url = `${OrderService.BASE_URL}/public/user/items/sold/stats`;
    return apiService.get(url, { userId: userId }, {});
  }

  createPurchase(newPurchase: NewPurchaseDto): AxiosPromise<PurchaseDto> {
    const url = `${OrderService.BASE_URL}/purchases`;
    return apiService.post(url, newPurchase);
  }

  updatePurchase(id: number, purchaseUpdate: PurchaseUpdateDto): AxiosPromise<PurchaseDto> {
    const url = `${OrderService.BASE_URL}/purchases/${id}`;
    return apiService.patch(url, purchaseUpdate);
  }

  fetchPurchaseDetails(orderId: number, authToken?: string): AxiosPromise<PurchaseDto> {
    const url = `${OrderService.BASE_URL}/purchases/${orderId}`;
    return apiService.get(url, {}, { [HEADER_AUTH_TOKEN]: authToken });
  }

  markAsPickedUp(orderId: number) {
    const url = `${OrderService.BASE_URL}/purchases/${orderId}`;
    return apiService.patch(url, { status: 'PICKED_UP' });
  }

  fetchSafetySpot(
    centerLatitude: number,
    centerLongitude: number,
    latitudeDelta: number,
    longitudeDelta: number
  ): AxiosPromise<SafetySpotDto[]> {
    const url = `${OrderService.BASE_URL}/safety-spots`;
    return apiService.get(url, { centerLatitude, centerLongitude, latitudeDelta, longitudeDelta });
  }

  fetchMyItemsStats(): AxiosPromise<ItemStatsDto> {
    const url = `${OrderService.BASE_URL}/user/items/stats`;
    return apiService.get(url, {}, {});
  }

  fetchMyBoughtItems(page: number, size: number, sort?: string): AxiosPromise<PageableType<BoughtItemDto>> {
    const url = `${OrderService.BASE_URL}/user/items/bought`;
    return apiService.get(url, { page: page, size: size, sort: sort ? sort : 'boughtDate,desc' }, {});
  }

  fetchMySoldItems(page: number, size: number, sort?: string): AxiosPromise<PageableType<SoldItemDto>> {
    const url = `${OrderService.BASE_URL}/user/items/sold`;
    return apiService.get(url, { page: page, size: size, sort: sort ? sort : 'soldDate,desc' }, {});
  }

  proposeMeetupPoint(orderId: number, meetupPoint: MeetupPointDto) {
    const payload = {
      orderId: orderId,
      safetySpotId: meetupPoint.id,
    };
    return apiService.post(`${OrderService.BASE_URL}/meetup-points`, payload);
  }

  fetchMyPurchases(itemId: number): AxiosPromise<PurchaseDto[]> {
    const url = `${OrderService.BASE_URL}/my-purchases`;
    return apiService.get(url, { itemId: itemId }, {});
  }

  fetchMySellings(itemId: number): AxiosPromise<PurchaseDto[]> {
    const url = `${OrderService.BASE_URL}/my-sellings`;
    return apiService.get(url, { itemId: itemId }, {});
  }

  cancelPurchase(id: number) {
    const url = `${OrderService.BASE_URL}/purchases/${id}`;
    return apiService.patch(url, { status: 'CANCELED' });
  }

  requestForRefund(orderId: number, reason: string) {
    const url = `${OrderService.BASE_URL}/refund-requests`;
    return apiService.post(url, { orderId, reason });
  }

  reassignBuyer(orderId: number, reassignmentToken: string) {
    const url = `${OrderService.BASE_URL}/purchases/${orderId}/buyer`;
    return apiService.post(url, { reassignmentToken });
  }

  fetchClientConfiguration(currencyCode: string): AxiosPromise<OrderConfigDto> {
    const url = `${OrderService.BASE_URL}/public/client-configuration`;
    return apiService.get(url, { currencyCode }, {});
  }
}

const orderService = new OrderService();

export { orderService, OrderService };
